import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { AlertTriangle, ArrowLeft, MessageCircle } from "lucide-react";
import { api, formatApiError } from "../../api";
import { TRAINER } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { GymLoader } from "../../components/common/GymLoader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "../../components/ui/sonner";

const PROGRESS_TYPES = ["weight", "measurement", "pr", "assessment", "streak"];
const NOTE_CATEGORIES = ["injury", "goal", "preference", "general"];

function fmtDate(iso) { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }

// UX-TR-01 §1 SCR-P06-21: a client-supplied `memberId` that isn't actually assigned to this
// trainer 403s server-side (`_require_own_client`) — this screen relies on that, it never
// filters a wider list client-side.
export default function ClientDetail() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  const qc = useQueryClient();

  const [progressType, setProgressType] = useState("weight");
  const [progressValue, setProgressValue] = useState("");
  const [progressUnit, setProgressUnit] = useState("kg");
  const [programStructure, setProgramStructure] = useState('{"weeks": 8, "focus": "strength"}');
  const [noteCategory, setNoteCategory] = useState("general");
  const [noteText, setNoteText] = useState("");

  const detailQ = useQuery({
    queryKey: ["trainer-client", memberId],
    queryFn: async () => (await api.get(`/trainer/clients/${memberId}`)).data,
    retry: false,
  });

  const progressMutation = useMutation({
    mutationFn: () => api.post(`/trainer/clients/${memberId}/progress`, { type: progressType, value: Number(progressValue), unit: progressUnit || null }),
    onSuccess: () => { toast.success("Progress logged."); setProgressValue(""); qc.invalidateQueries({ queryKey: ["trainer-client", memberId] }); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const programMutation = useMutation({
    mutationFn: () => {
      let structure;
      try { structure = JSON.parse(programStructure); } catch { throw new Error("Program structure must be valid JSON."); }
      return api.post(`/trainer/clients/${memberId}/program`, { structure });
    },
    onSuccess: () => { toast.success("Program assigned."); qc.invalidateQueries({ queryKey: ["trainer-client", memberId] }); },
    onError: (e) => toast.error(e.response ? formatApiError(e).message : e.message),
  });

  const noteMutation = useMutation({
    mutationFn: () => api.post(`/trainer/clients/${memberId}/notes`, { note: noteText, category: noteCategory }),
    onSuccess: () => { toast.success("Note added."); setNoteText(""); qc.invalidateQueries({ queryKey: ["trainer-client", memberId] }); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  if (detailQ.isLoading) return <GymLoader fullPage label="Loading client…" testId="trainer-client-detail-loader" />;

  // §0.1: a client-supplied memberId that isn't actually assigned to this trainer 403s
  // server-side — render an explicit blocked state, never a mostly-empty-but-interactive shell
  // that lets forms be submitted against data this trainer was denied.
  if (detailQ.isError) {
    return (
      <div data-testid={TRAINER.clientDetailPage} className="space-y-4 max-w-2xl">
        <button data-testid={TRAINER.clientDetailBackButton} onClick={() => navigate("/trainer/clients")} className="flex items-center gap-1.5 text-sm font-medium" style={{ color: "#68686E" }}>
          <ArrowLeft size={14} /> Back
        </button>
        <div className="rounded-[14px] p-6 text-center" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <AlertTriangle size={22} style={{ color: "var(--scarlet-neg)" }} className="mx-auto mb-2" />
          <p className="text-sm font-semibold" style={{ color: "#141416" }}>You don't have access to this client</p>
          <p data-testid="trainer-client-detail-error" className="text-xs mt-1" style={{ color: "#68686E" }}>{formatApiError(detailQ.error).message}</p>
        </div>
      </div>
    );
  }
  const client = detailQ.data;

  return (
    <div data-testid={TRAINER.clientDetailPage} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)} className="flex items-center justify-between">
        <button data-testid={TRAINER.clientDetailBackButton} onClick={() => navigate("/trainer/clients")} className="flex items-center gap-1.5 text-sm font-medium" style={{ color: "#68686E" }}>
          <ArrowLeft size={14} /> Back
        </button>
        <Button data-testid={TRAINER.messageClientButton} size="sm" variant="outline" onClick={() => navigate(`/trainer/message?client=${memberId}`)} className="gap-1.5">
          <MessageCircle size={13} /> Message
        </Button>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.02)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>{client?.name}</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>{client?.phone}</p>
        {client?.slipping && (
          <p className="flex items-center gap-1.5 text-sm font-medium mt-2" style={{ color: "var(--scarlet-neg)" }}>
            <AlertTriangle size={14} /> {client.slipping_reason}
          </p>
        )}
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <p className="text-sm font-semibold" style={{ color: "#141416" }}>Log progress</p>
        <div className="flex gap-2">
          <Select onValueChange={setProgressType} value={progressType}>
            <SelectTrigger data-testid={TRAINER.progressTypeSelect}><SelectValue /></SelectTrigger>
            <SelectContent>{PROGRESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid={TRAINER.progressValueInput} type="number" placeholder="Value" value={progressValue} onChange={(e) => setProgressValue(e.target.value)} />
          <Input data-testid={TRAINER.progressUnitInput} placeholder="Unit (optional)" value={progressUnit} onChange={(e) => setProgressUnit(e.target.value)} />
        </div>
        <Button data-testid={TRAINER.progressSubmitButton} disabled={!progressValue || progressMutation.isPending} onClick={() => progressMutation.mutate()} className="w-full text-white" style={{ background: "#D6202B" }}>
          Log entry
        </Button>
        <div className="pt-2 space-y-1.5">
          {(client?.progress || []).slice().reverse().slice(0, 8).map((p) => (
            <div key={p.id} data-testid={TRAINER.progressEntryRow(p.id)} className="flex items-center justify-between text-sm">
              <span style={{ color: "#68686E" }}>{fmtDate(p.at)} · {p.type}</span>
              <span style={{ color: "#141416" }}>{p.value} {p.unit || ""}</span>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.06)} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <p className="text-sm font-semibold" style={{ color: "#141416" }}>Program</p>
        {(client?.programs || []).map((p) => (
          <div key={p.id} data-testid={TRAINER.programRow(p.id)} className="text-xs rounded-[8px] px-3 py-2" style={{ background: "#F4F3EF", color: "#141416" }}>
            <pre className="whitespace-pre-wrap font-mono">{JSON.stringify(p.structure, null, 1)}</pre>
            <span className="status-pill active mt-1">{p.status}</span>
          </div>
        ))}
        <Input data-testid={TRAINER.programStructureInput} value={programStructure} onChange={(e) => setProgramStructure(e.target.value)} placeholder='{"weeks": 8, "focus": "strength"}' />
        <Button data-testid={TRAINER.programAssignButton} disabled={programMutation.isPending} onClick={() => programMutation.mutate()} variant="outline" className="w-full">
          Assign new program
        </Button>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.08)} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <p className="text-sm font-semibold" style={{ color: "#141416" }}>Notes</p>
        {(client?.notes || []).slice().reverse().map((n, i) => (
          <p key={i} data-testid={TRAINER.noteRow(i)} className="text-sm" style={{ color: "#141416" }}>{n.text}</p>
        ))}
        <div className="flex gap-2">
          <Select onValueChange={setNoteCategory} value={noteCategory}>
            <SelectTrigger data-testid={TRAINER.noteCategorySelect}><SelectValue /></SelectTrigger>
            <SelectContent>{NOTE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid={TRAINER.noteInput} placeholder="e.g. left knee, avoid heavy squats" value={noteText} onChange={(e) => setNoteText(e.target.value)} />
        </div>
        <Button data-testid={TRAINER.noteAddButton} disabled={!noteText.trim() || noteMutation.isPending} onClick={() => noteMutation.mutate()} variant="outline" className="w-full">
          Add note
        </Button>
      </motion.div>
    </div>
  );
}
